import React, { useState, useEffect, useRef } from 'react';
import { 
  Droplets, 
  AlertTriangle, 
  Gauge, 
  Timer,
  TrendingUp,
  TrendingDown,
  Activity,
  MapPin,
  Zap
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useIntlFormatting } from '../utils/intl';
import { iotApi, DashboardStats, IoTNode } from '../lib/iotApi';
import { getNodeFlow, getPressure, getSoilMoisture, getPumpState } from '../lib/liveSensorMetrics';
import { IS_AWS_MODE } from '../config/frontend';

interface KPICard {
  title: string;
  value: string | number;
  unit: string;
  change: number;
  changeLabel: string;
  icon: React.ElementType;
  color: string;
}

interface ChartData {
  time: string;
  moisture: number;
  pressure: number;
  flow: number;
}

// ---------------------------------------------------------------------------
// FIELD-UI-B2 — farmer read-only dashboard data derivation (AWS mode only)
// ---------------------------------------------------------------------------

const UNKNOWN = '—';

/** Freshest valid communication timestamp across a node's fields (epoch ms), or null. */
const nodeTimestampMs = (node: IoTNode): number | null => {
  const candidates = [node.last_seen_at, node.latest?.ts]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .map((value) => Date.parse(value))
    .filter((ms) => Number.isFinite(ms));
  return candidates.length > 0 ? Math.max(...candidates) : null;
};

/**
 * Select the freshest available value for a metric across nodes using a
 * node-aware getter. Only a finite value (including a real 0) is selected;
 * absence yields null.
 */
const selectFreshestMetric = (
  nodes: IoTNode[],
  getter: (node: IoTNode) => number | undefined,
): number | null => {
  let bestValue: number | null = null;
  let bestTs: number | null = null;
  for (const node of nodes) {
    const value = getter(node);
    if (value === undefined) continue;
    const ts = nodeTimestampMs(node) ?? -Infinity;
    if (bestTs === null || ts > bestTs) {
      bestTs = ts;
      bestValue = value;
    }
  }
  return bestValue;
};

/** Freshest valid communication timestamp across all nodes (epoch ms), or null. */
const latestCommunicationMs = (nodes: IoTNode[]): number | null => {
  let max: number | null = null;
  for (const node of nodes) {
    const ts = nodeTimestampMs(node);
    if (ts !== null && (max === null || ts > max)) max = ts;
  }
  return max;
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['dashboard', 'common']);
  const { formatNumber, formatDateTime } = useIntlFormatting();
  const [timeRange, setTimeRange] = useState('24h');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [nodes, setNodes] = useState<IoTNode[]>([]);
  const [loadError, setLoadError] = useState(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!IS_AWS_MODE) return;
    let active = true;
    let timer: number | undefined;

    // Non-overlapping refresh: schedule the next load only after the previous
    // settles, so a slow request can never pile up concurrent calls.
    const load = () => {
      Promise.all([
        iotApi.getJson<DashboardStats>('/iot/dashboard'),
        iotApi.getJson<IoTNode[]>('/iot/nodes'),
      ])
        .then(([dashStats, nodeList]) => {
          if (!active) return;
          hasLoadedRef.current = true;
          setStats(dashStats);
          setNodes(nodeList);
          setLoadError(false);
        })
        .catch(() => {
          if (!active) return;
          // A failed refresh must NOT erase previously-loaded valid data.
          if (!hasLoadedRef.current) setLoadError(true);
        })
        .finally(() => {
          if (active) timer = window.setTimeout(load, 30000);
        });
    };

    load();

    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  if (IS_AWS_MODE) {
    const flow = selectFreshestMetric(nodes, getNodeFlow);
    const pressure = selectFreshestMetric(nodes, (node) => getPressure(node.latest?.measures));
    const moisture = selectFreshestMetric(nodes, (node) => getSoilMoisture(node.latest?.measures));
    const pumpValue = selectFreshestMetric(nodes, (node) => getPumpState(node.latest?.measures));
    const lastCommMs = latestCommunicationMs(nodes);

    const pumpLabel =
      pumpValue === 1 ? t('dashboard:pump.on')
      : pumpValue === 0 ? t('dashboard:pump.off')
      : UNKNOWN;

    const displayNumber = (value: number | null, options?: Intl.NumberFormatOptions): string =>
      value === null ? UNKNOWN : formatNumber(value, options);

    return (
      <div className="space-y-6" data-testid="farmer-dashboard">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]" data-testid="field-heading">
            {t('dashboard:farmerTitle')}
          </h1>
          <p className="text-body text-[var(--text-secondary)] mt-1" data-testid="field-subtitle">
            {t('dashboard:farmerSubtitle')}
          </p>
        </div>

        {loadError && (
          <div
            className="card border border-[var(--color-danger)] text-[var(--color-danger)]"
            data-testid="dashboard-error"
          >
            {t('dashboard:farmerLoadError')}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Installation */}
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-5 h-5 text-[var(--color-primary)]" />
              <h2 className="text-heading-4 text-[var(--text-primary)]">{t('dashboard:section.installation')}</h2>
            </div>
            <dl className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-sm text-[var(--text-secondary)]">{t('dashboard:field.onlineEquipment')}</dt>
                <dd className="font-semibold text-[var(--text-primary)]" data-testid="install-online">
                  {t('dashboard:field.onlineEquipmentValue', {
                    online: displayNumber(stats?.nodes_online ?? null),
                    total: displayNumber(stats?.nodes_total ?? null),
                  })}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-sm text-[var(--text-secondary)]">{t('dashboard:field.lastCommunication')}</dt>
                <dd className="font-semibold text-[var(--text-primary)]" data-testid="install-last-comm">
                  {lastCommMs !== null ? formatDateTime(lastCommMs) : UNKNOWN}
                </dd>
              </div>
            </dl>
          </div>

          {/* Irrigation */}
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <Droplets className="w-5 h-5 text-[var(--color-info)]" />
              <h2 className="text-heading-4 text-[var(--text-primary)]">{t('dashboard:section.irrigation')}</h2>
            </div>
            <dl className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-sm text-[var(--text-secondary)]">{t('dashboard:field.valvesOpen')}</dt>
                <dd className="font-semibold text-[var(--text-primary)]" data-testid="irrigation-valves-open">
                  {displayNumber(stats?.valves_open ?? null)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Mesures */}
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="w-5 h-5 text-[var(--color-success)]" />
              <h2 className="text-heading-4 text-[var(--text-primary)]">{t('dashboard:section.measures')}</h2>
            </div>
            <dl className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-sm text-[var(--text-secondary)]">{t('dashboard:field.flow')}</dt>
                <dd className="font-semibold text-[var(--text-primary)]">
                  <span data-testid="measure-flow">{displayNumber(flow, { maximumFractionDigits: 1 })}</span>
                  <span className="text-xs text-[var(--text-tertiary)] ml-1">{t('dashboard:unit.flow')}</span>
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-sm text-[var(--text-secondary)]">{t('dashboard:field.pressure')}</dt>
                <dd className="font-semibold text-[var(--text-primary)]">
                  <span data-testid="measure-pressure">{displayNumber(pressure, { maximumFractionDigits: 1 })}</span>
                  <span className="text-xs text-[var(--text-tertiary)] ml-1">{t('dashboard:unit.pressure')}</span>
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-sm text-[var(--text-secondary)]">{t('dashboard:field.soilMoisture')}</dt>
                <dd className="font-semibold text-[var(--text-primary)]">
                  <span data-testid="measure-moisture">{displayNumber(moisture, { maximumFractionDigits: 1 })}</span>
                  <span className="text-xs text-[var(--text-tertiary)] ml-1">{t('dashboard:unit.moisture')}</span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Pompage */}
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-[var(--color-warning)]" />
              <h2 className="text-heading-4 text-[var(--text-primary)]">{t('dashboard:section.pumping')}</h2>
            </div>
            <dl className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-sm text-[var(--text-secondary)]">{t('dashboard:field.pumpState')}</dt>
                <dd className="font-semibold text-[var(--text-primary)]" data-testid="pump-state">
                  {pumpLabel}
                </dd>
              </div>
            </dl>
          </div>

          {/* Alertes */}
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-[var(--color-warning)]" />
              <h2 className="text-heading-4 text-[var(--text-primary)]">{t('dashboard:section.alerts')}</h2>
            </div>
            <dl className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <dt className="text-sm text-[var(--text-secondary)]">{t('dashboard:field.activeAlerts')}</dt>
                <dd className="font-semibold text-[var(--text-primary)]" data-testid="alerts-open">
                  {displayNumber(stats?.open_alerts ?? null)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    );
  }

  // Mock KPI data
  const kpis: KPICard[] = [
    {
      title: t('dashboard:kpis.waterSaved.title'),
      value: formatNumber(1247),
      unit: t('dashboard:kpis.waterSaved.unit'),
      change: 12.5,
      changeLabel: t('dashboard:kpis.waterSaved.comparison'),
      icon: Droplets,
      color: 'var(--color-info)',
    },
    {
      title: t('dashboard:kpis.activeAlerts.title'),
      value: 3,
      unit: t('dashboard:kpis.activeAlerts.unit'),
      change: -2,
      changeLabel: t('dashboard:kpis.activeAlerts.comparison'),
      icon: AlertTriangle,
      color: 'var(--color-warning)',
    },
    {
      title: t('dashboard:kpis.avgMoisture.title'),
      value: 68,
      unit: t('dashboard:kpis.avgMoisture.unit'),
      change: 5.2,
      changeLabel: t('dashboard:kpis.avgMoisture.comparison', { target: 65 }),
      icon: Gauge,
      color: 'var(--color-success)',
    },
    {
      title: t('dashboard:kpis.pumpRuntime.title'),
      value: formatNumber(24.7, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      unit: t('dashboard:kpis.pumpRuntime.unit'),
      change: -8.3,
      changeLabel: t('dashboard:kpis.pumpRuntime.comparison'),
      icon: Timer,
      color: 'var(--color-primary)',
    },
  ];

  // Mock chart data
  const chartData: ChartData[] = [
    { time: '00:00', moisture: 65, pressure: 2.4, flow: 3.2 },
    { time: '04:00', moisture: 62, pressure: 2.8, flow: 4.1 },
    { time: '08:00', moisture: 58, pressure: 3.2, flow: 5.8 },
    { time: '12:00', moisture: 54, pressure: 3.6, flow: 6.2 },
    { time: '16:00', moisture: 61, pressure: 3.1, flow: 4.9 },
    { time: '20:00', moisture: 67, pressure: 2.6, flow: 3.4 },
  ];

  const recentAlerts = [
    {
      id: '1',
      type: 'leak',
      zone: 'Zone A-1',
      message: 'Potential leak detected: flow rate 7.2 m³/h exceeds baseline',
      time: '2 hours ago',
      severity: 'critical'
    },
    {
      id: '2', 
      type: 'low_moisture',
      zone: 'Zone B-3',
      message: 'Low soil moisture: 45% below target minimum 60%',
      time: '4 hours ago',
      severity: 'warning'
    },
    {
      id: '3',
      type: 'ph_out_of_range',
      zone: 'Zone C-2',
      message: 'pH out of range: 8.2 (optimal: 5.5-7.8)',
      time: '6 hours ago',
      severity: 'info'
    }
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'var(--color-danger)';
      case 'warning': return 'var(--color-warning)';
      case 'info': return 'var(--color-info)';
      default: return 'var(--text-secondary)';
    }
  };

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-primary rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-10 rounded-full transform translate-x-8 -translate-y-8"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white bg-opacity-10 rounded-full transform -translate-x-4 translate-y-4"></div>
        <div className="relative z-10">
          <h1 className="text-heading-1 mb-2">
            {t('dashboard:welcomeBack', { name: user?.name?.split(' ')[0] || 'User' })} 👋
          </h1>
          <p className="text-lg opacity-90 mb-4">
            {t('dashboard:systemStatus', { count: 12 })}
          </p>
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5" />
              <span className="text-sm">{t('dashboard:allSystemsOperational')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5" />
              <span className="text-sm">{t('dashboard:regionLocation')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {kpis.map((kpi, index) => (
          <div key={index} className="card hover:shadow-lg" data-testid={`kpi-card-${index}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${kpi.color}15` }}>
                <kpi.icon className="w-6 h-6" style={{ color: kpi.color }} />
              </div>
              <div className={`flex items-center space-x-1 text-sm ${
                kpi.change >= 0 ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'
              }`}>
                {kpi.change >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                <span className="font-medium">{Math.abs(kpi.change)}%</span>
              </div>
            </div>
            <div className="mb-2">
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-[var(--text-primary)]">{kpi.value}</span>
                <span className="text-sm text-[var(--text-secondary)]">{kpi.unit}</span>
              </div>
            </div>
            <div className="text-xs text-[var(--text-secondary)]">
              {kpi.changeLabel}
            </div>
            <div className="text-sm font-medium text-[var(--text-primary)] mt-1">
              {kpi.title}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Moisture & Pressure Chart */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-heading-3 text-[var(--text-primary)]">
              {t('dashboard:charts.systemMonitoring.title')}
            </h2>
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
              className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
              data-testid="time-range-select"
            >
              <option value="24h">{t('dashboard:charts.systemMonitoring.last24h')}</option>
              <option value="7d">{t('dashboard:charts.systemMonitoring.last7d')}</option>
              <option value="30d">{t('dashboard:charts.systemMonitoring.last30d')}</option>
            </select>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="time" stroke="var(--text-secondary)" />
                <YAxis stroke="var(--text-secondary)" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--surface)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    boxShadow: 'var(--shadow-lg)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="moisture" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2}
                  name={t('dashboard:charts.systemMonitoring.moisture')}
                />
                <Line 
                  type="monotone" 
                  dataKey="pressure" 
                  stroke="var(--color-info)" 
                  strokeWidth={2}
                  name={t('dashboard:charts.systemMonitoring.pressure')}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Flow Rate Chart */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-heading-3 text-[var(--text-primary)]">
              {t('dashboard:charts.flowRate.title')}
            </h2>
            <div className="text-sm text-[var(--text-secondary)]">{t('dashboard:charts.flowRate.unit')}</div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="time" stroke="var(--text-secondary)" />
                <YAxis stroke="var(--text-secondary)" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--surface)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    boxShadow: 'var(--shadow-lg)'
                  }}
                />
                <Bar 
                  dataKey="flow" 
                  fill="var(--color-accent)" 
                  radius={[4, 4, 0, 0]}
                  name={t('dashboard:charts.flowRate.flowRate')}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-heading-3 text-[var(--text-primary)]">
            {t('dashboard:recentAlerts.title')}
          </h2>
          <button className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-700)] font-medium">
            {t('dashboard:recentAlerts.viewAll')}
          </button>
        </div>
        <div className="space-y-4">
          {recentAlerts.map((alert) => (
            <div key={alert.id} className="flex items-start space-x-4 p-4 rounded-lg border border-[var(--border-light)] hover:bg-[var(--gray-50)] transition-colors">
              <div 
                className="w-2 h-2 rounded-full mt-2"
                style={{ backgroundColor: getSeverityColor(alert.severity) }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {alert.zone}
                  </p>
                  <span className="text-xs text-[var(--text-tertiary)]">
                    {alert.time}
                  </span>
                </div>
                <p className="text-sm text-[var(--text-secondary)]">
                  {alert.message}
                </p>
              </div>
              <button className="text-xs px-3 py-1 rounded-full border border-[var(--border-medium)] hover:bg-[var(--gray-100)] transition-colors">
                {t('dashboard:recentAlerts.acknowledge')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center">
          <div className="w-12 h-12 bg-[var(--color-primary-100)] rounded-lg flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-6 h-6 text-[var(--color-primary)]" />
          </div>
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">{t('dashboard:quickActions.viewMap.title')}</h3>
          <p className="text-body-sm text-[var(--text-secondary)] mb-4">
            {t('dashboard:quickActions.viewMap.description')}
          </p>
          <button className="btn-secondary w-full" data-testid="view-map-button">
            {t('dashboard:quickActions.viewMap.button')}
          </button>
        </div>

        <div className="card text-center">
          <div className="w-12 h-12 bg-[var(--color-warning-100)] rounded-lg flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-[var(--color-warning)]" />
          </div>
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">{t('dashboard:quickActions.manageAlerts.title')}</h3>
          <p className="text-body-sm text-[var(--text-secondary)] mb-4">
            {t('dashboard:quickActions.manageAlerts.description')}
          </p>
          <button className="btn-secondary w-full" data-testid="manage-alerts-button">
            {t('dashboard:quickActions.manageAlerts.button')}
          </button>
        </div>

        <div className="card text-center">
          <div className="w-12 h-12 bg-[var(--color-info-100)] rounded-lg flex items-center justify-center mx-auto mb-4">
            <Zap className="w-6 h-6 text-[var(--color-info)]" />
          </div>
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">{t('dashboard:quickActions.deviceStatus.title')}</h3>
          <p className="text-body-sm text-[var(--text-secondary)] mb-4">
            {t('dashboard:quickActions.deviceStatus.description')}
          </p>
          <button className="btn-secondary w-full" data-testid="device-status-button">
            {t('dashboard:quickActions.deviceStatus.button')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
